import { z } from 'zod'

import { Client, ClientBinding } from './client'

import { Pagination } from 'app/types/app/request'
import { Manager } from 'app/types/park/manager'
import { KonsoleUser } from 'app/types/users/konsole'

/** Type of site */
export const SiteType = z.object({
    id: z.number().positive(),
    name: z.string().min(1),
    client: Manager,
    priority: z.number(),
})

export type SiteType = z.infer<typeof SiteType>

export const SiteTypeRequest = Pagination.extend({
    results: SiteType.array(),
})

export type SiteTypeRequest = z.infer<typeof SiteTypeRequest>

/** Area where the site is. */
export const Zone = z.object({
    id: z.number().positive(),
    name: z.string().min(1),
    client: Manager,
    priority: z.number(),
})
export type Zone = z.infer<typeof Zone>

export const ZoneRequest = Pagination.extend({
    results: Zone.array(),
})

export type ZoneRequest = z.infer<typeof ZoneRequest>

/** Country enum. */
export const Country = z.enum([
    'BELGIUM',
    'FRANCE',
    'GERMANY',
    'ITALY',
    'LUXEMBOURG',
    'NETHERLANDS',
    'PORTUGAL',
    'SPAIN',
    'SWITZERLAND',
    'UNITED_KINGDOM',
    'UNITED_STATES_OF_AMERICA',
])

export type Country = z.infer<typeof Country>

/** Site of a client that contains machines. */
export const Site = z.object({
    /** ID of the site. */
    id: z.number().positive(),

    /** Name of the site. */
    name: z.string().min(1),

    /** Client related. */
    customer_name: ClientBinding,

    /** The supervisor of site. */
    supervisor: KonsoleUser,

    /** Site type */
    site_type: SiteType,

    /** Area of the site. */
    zone: Zone,

    /** Description of the site. */
    description: z.string().nullable(),

    /** Address of the site. */
    address: z.string().min(1),

    /** City of the site. */
    city: z.string().min(1),

    /** Country of the site. */
    country: Country,

    /** Postal code of the site. */
    postal_code: z.string().min(1),

    /** Latitude of the site. */
    latitude: z.number(),

    /** Longitude of the site. */
    longitude: z.number(),

    /** Building floor of the site. */
    building_floor: z.string().nullable(),

    /** Further information about the site. */
    further_information: z.string().nullable(),

    /** Supply days of the site. */
    supply_days: z.string(),

    /** Recommended hour to supply. */
    recommended_delivery_time: z.string().nullable(),

    /** The opening time of the site. */
    opening_hours: z.string().nullable(),

    /** The closing time of the site. */
    closing_hours: z.string().nullable(),

    /** Know if the site can have order. */
    has_order: z.boolean(),
})

export type Site = z.infer<typeof Site>

export const SiteBinding = Site.pick({
    id: true,
    name: true,
    customer_name: true,
})

export type SiteBinding = z.infer<typeof SiteBinding>

/** The data send when creating or updating a site. */
export const SitePayload = Site.omit({
    id: true,
    customer_name: true,
    site_type: true,
    zone: true,
    supervisor: true,
}).extend({
    customer_name: Client.shape.id,
    site_type: SiteType.shape.id,
    zone: Zone.shape.id,
    supervisor: KonsoleUser.shape.id,
})

export type SitePayload = z.infer<typeof SitePayload>

export const SitesRequest = Pagination.extend({
    results: Site.array(),
})

export type SitesRequest = z.infer<typeof SitesRequest>
